import { toMentionContent, toTextContent } from '@principle-theorem/editor';
import {
  AccAddressType,
  AccPatientFormType,
  AddressComponents,
  FundingRequestProvider,
  FundingRequestStatus,
  FundingRequestType,
  IAccAddress,
  IAccClaimFundingRequest,
  InteractionType,
  IPatient,
  IPractice,
  IStaffer,
  MentionResourceType,
  PatientFormStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  addDocAsWithRef,
  asyncForEach,
  AtLeast,
  Firestore,
  IReffable,
  multiUndeleted,
  snapshot,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { Address } from '../../address/address';
import { Interaction } from '../../interaction/interaction';
import { toMention } from '../../mention/mention';
import { Patient } from '../../patient/patient';
import { PatientForm } from '../../patient/patient-form';
import { Practice } from '../../practice/practice';
import { FundingRequest } from '../funding-request';

export class AccClaimFundingRequest {
  static init(
    overrides: AtLeast<IAccClaimFundingRequest, 'data' | 'patient'>
  ): IAccClaimFundingRequest {
    return FundingRequest.init({
      fundingProvider: FundingRequestProvider.ACC,
      type: FundingRequestType.PreApproval,
      ...overrides,
    });
  }

  static all$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IAccClaimFundingRequest>[]> {
    return Firestore.query$(
      Practice.fundingRequestCol<IAccClaimFundingRequest>(practice),
      where('fundingProvider', '==', FundingRequestProvider.ACC),
      where('type', '==', FundingRequestType.PreApproval)
    ).pipe(multiUndeleted());
  }

  static byPatient$(
    practice: IReffable<IPractice>,
    patient: IReffable<IPatient>
  ): Observable<WithRef<IAccClaimFundingRequest>[]> {
    return Firestore.query$(
      Practice.fundingRequestCol<IAccClaimFundingRequest>(practice),
      where('fundingProvider', '==', FundingRequestProvider.ACC),
      where('type', '==', FundingRequestType.PreApproval),
      where('patient.ref', '==', patient.ref)
    ).pipe(multiUndeleted());
  }

  static formatAddress(
    type: AccAddressType,
    components?: AddressComponents
  ): IAccAddress {
    return {
      type,
      line1: Address.streetAddressFromComponents(components) ?? '',
      line2: components?.subpremise ?? '',
      suburb: components?.suburb ?? '',
      city: components?.city ?? '',
      postCode: components?.postalCode ?? '',
      country: components?.country ?? '',
    };
  }

  static async issuePatientForms(
    patient: WithRef<IPatient>,
    claim: WithRef<IAccClaimFundingRequest>,
    staffer: WithRef<IStaffer>,
    formsToIssue: AccPatientFormType[]
  ): Promise<void> {
    await asyncForEach(formsToIssue, async (formType) => {
      const result = await addDocAsWithRef(
        Patient.formCol(patient),
        PatientForm.init({
          formType,
          associatedDocument: claim.ref,
        })
      );

      const interaction = Interaction.init({
        title: [
          toMentionContent(toMention(staffer, MentionResourceType.Staffer)),
          toTextContent(' generated the form'),
        ],
        type: InteractionType.FormIssue,
      });

      await PatientForm.addInteraction(result, interaction);
    });
  }

  static async updateIssuedPatientForms(
    patient: WithRef<IPatient>,
    claim: WithRef<IAccClaimFundingRequest> | undefined,
    formsToRemove: AccPatientFormType[] = []
  ): Promise<void> {
    if (
      !claim ||
      !AccClaimFundingRequest.isClaimWithIssuedPatientForms(claim)
    ) {
      return;
    }

    const forms = await snapshot(
      PatientForm.byAssociatedDocument$(patient, claim.ref, formsToRemove)
    );

    if (!forms.length) {
      return;
    }

    await asyncForEach(forms, async (form) => {
      if (form.status === PatientFormStatus.Issued) {
        await Firestore.deleteDoc(form.ref);
      } else if (form.status === PatientFormStatus.Submitted) {
        await Firestore.patchDoc(form.ref, {
          ...PatientForm.updateStatus(form, PatientFormStatus.Confirmed),
        });
      }
    });
  }

  /**
   * A claim can be edited and (re)submitted while it is a Draft or has Failed
   * submission to ACC. Failed is treated as editable so staff can correct the
   * claim and retry, rather than being permanently locked out.
   */
  static isClaimEditable(claim: WithRef<IAccClaimFundingRequest>): boolean {
    return [FundingRequestStatus.Draft, FundingRequestStatus.Failed].includes(
      claim.status
    );
  }

  static resetFailedToDraft(
    claim: WithRef<IAccClaimFundingRequest>
  ): WithRef<IAccClaimFundingRequest> {
    if (claim.status !== FundingRequestStatus.Failed) {
      return claim;
    }

    return {
      ...claim,
      status: FundingRequestStatus.Draft,
      errorMessage: undefined,
    };
  }

  static isClaimWithIssuedPatientForms(
    claim: WithRef<IAccClaimFundingRequest>
  ): boolean {
    const patientForms = claim.data.metadata.patientForms;
    return patientForms
      ? Object.values(patientForms).some(
          (form) => form.selected && !form.submittedAt
        )
      : false;
  }
}
